import { useCallback, useEffect, useState } from 'react'
import { traduccionesApi } from '../api/traducciones'
import Boton from '../components/Boton'
import { colors, font, text, space, radius } from '../design/tokens'

/**
 * Traducciones de la tienda (Martin, 24 sep 2026). Lo que se escribe en
 * castellano en el panel (banners de categoría, carrusel de la home,
 * experiencias) lo traduce la IA sola al guardarlo; acá se ve cada texto con
 * su traducción al inglés y se corrige. Una traducción corregida queda
 * "Corregida" y la IA no la vuelve a pisar.
 */
export default function TraduccionesWebPage() {
  const [filas, setFilas] = useState([])
  const [pendientes, setPendientes] = useState(0)
  const [automatica, setAutomatica] = useState(false)
  const [q, setQ] = useState('')
  const [cargando, setCargando] = useState(true)
  const [editando, setEditando] = useState({}) // id → texto en edición
  const [guardando, setGuardando] = useState(null)
  const [traduciendo, setTraduciendo] = useState(false)
  const [msg, setMsg] = useState('')

  const cargar = useCallback((busqueda = '') => {
    setCargando(true)
    traduccionesApi.listar(busqueda)
      .then(({ data }) => {
        setFilas(data.data)
        setPendientes(data.pendientes)
        setAutomatica(data.automatica)
      })
      .catch(() => setMsg('No se pudieron cargar las traducciones.'))
      .finally(() => setCargando(false))
  }, [])
  useEffect(() => { cargar() }, [cargar])

  const guardar = async (fila) => {
    const texto = (editando[fila.id] ?? '').trim()
    if (!texto) return
    setGuardando(fila.id)
    try {
      const { data } = await traduccionesApi.corregir(fila.id, texto)
      setFilas((fs) => fs.map((f) => (f.id === fila.id ? { ...f, texto: data.data.texto, origen: data.data.origen } : f)))
      setEditando((e) => { const n = { ...e }; delete n[fila.id]; return n })
    } catch {
      setMsg('No se pudo guardar la corrección.')
    } finally {
      setGuardando(null)
    }
  }

  const traducirAhora = async () => {
    setTraduciendo(true)
    setMsg('')
    try {
      const { data } = await traduccionesApi.traducirPendientes()
      setMsg(`Traducidos: ${data.traducidos}. Quedan pendientes: ${data.pendientes}.${data.errores?.length ? ' ' + data.errores.join(' ') : ''}`)
      cargar(q)
    } catch {
      setMsg('No se pudo traducir ahora.')
    } finally {
      setTraduciendo(false)
    }
  }

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={st.head}>
        <h2 style={st.title}>Traducciones web</h2>
        {pendientes > 0 && (
          <Boton variante="primary" sm onClick={traducirAhora} disabled={traduciendo}>
            {traduciendo ? 'Traduciendo…' : `Traducir ${pendientes} pendiente${pendientes === 1 ? '' : 's'}`}
          </Boton>
        )}
      </div>
      <p style={st.sub}>
        Lo que cargás en castellano en banners, carrusel y experiencias se traduce solo al inglés
        {automatica ? ' al guardarlo' : ' (la traducción automática al guardar está apagada: usá el botón)'}.
        Si una traducción no te gusta, corregila acá: queda como tuya y no se vuelve a pisar.
      </p>

      <form onSubmit={(e) => { e.preventDefault(); cargar(q) }} style={{ display: 'flex', gap: space.sm, marginBottom: space.md }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar un texto…" style={st.input} />
        <Boton sm type="submit">Buscar</Boton>
      </form>

      {msg && <p style={st.msg}>{msg}</p>}

      {cargando ? (
        <p style={st.vacio}>Cargando…</p>
      ) : filas.length === 0 ? (
        <p style={st.vacio}>Todavía no hay traducciones.</p>
      ) : (
        <div style={st.tabla}>
          <div style={{ ...st.fila, ...st.cabecera }}>
            <span>Castellano</span>
            <span>Inglés</span>
            <span />
          </div>
          {filas.map((f) => {
            const enEdicion = editando[f.id] !== undefined
            return (
              <div key={f.id} style={st.fila}>
                <span style={st.es}>{f.texto_es}</span>
                {enEdicion ? (
                  <textarea
                    value={editando[f.id]}
                    onChange={(e) => setEditando((ed) => ({ ...ed, [f.id]: e.target.value }))}
                    rows={Math.min(8, Math.max(2, Math.ceil(editando[f.id].length / 60)))}
                    style={st.area}
                  />
                ) : (
                  <span style={st.en}>
                    {f.texto}
                    {f.origen === 'manual' && <span style={st.chip}>Corregida</span>}
                  </span>
                )}
                <span style={{ display: 'flex', gap: space.xs, justifyContent: 'flex-end' }}>
                  {enEdicion ? (
                    <>
                      <Boton sm variante="primary" onClick={() => guardar(f)} disabled={guardando === f.id}>Guardar</Boton>
                      <Boton sm onClick={() => setEditando((e) => { const n = { ...e }; delete n[f.id]; return n })}>Cancelar</Boton>
                    </>
                  ) : (
                    <Boton sm onClick={() => setEditando((e) => ({ ...e, [f.id]: f.texto }))}>Corregir</Boton>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const st = {
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: space.sm },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: `0 0 ${space.lg}px`, fontSize: text.sec, color: colors.textSec, maxWidth: 760, lineHeight: 1.5 },
  input: { flex: 1, maxWidth: 360, padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: text.body },
  msg: { fontSize: text.sec, color: colors.textSec, margin: `0 0 ${space.md}px` },
  vacio: { color: colors.textMeta, fontSize: text.sec },
  tabla: { border: `1px solid ${colors.border}`, borderRadius: radius.md, background: colors.bg, overflow: 'hidden' },
  fila: { display: 'grid', gridTemplateColumns: '1fr 1fr 170px', gap: space.md, padding: '10px 14px', borderBottom: `1px solid ${colors.borderSoft}`, alignItems: 'start' },
  cabecera: { background: colors.bgSoft, fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  es: { fontSize: text.body, color: colors.text, whiteSpace: 'pre-line', lineHeight: 1.4 },
  en: { fontSize: text.body, color: colors.text, whiteSpace: 'pre-line', lineHeight: 1.4 },
  area: { width: '100%', fontSize: text.body, fontFamily: font.sans, padding: 6, border: `1px solid ${colors.border}`, borderRadius: radius.sm, resize: 'vertical' },
  chip: { marginLeft: 8, fontSize: text.meta, fontWeight: 600, color: colors.ok, background: colors.okBg, borderRadius: radius.xs, padding: '1px 6px', whiteSpace: 'nowrap' },
}
